import React from "react";
import { IManageBusinessTaskProps } from "./ManageBusinessTask.types";

import { useFormik } from "formik";
import {
  Button,
  Grid,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import {
  IBusinessTask,
  INITIAL_BUSINESS_TASK,
} from "../../../redux/activity/activity.types";
import { useDispatchWrapper, usePoliciesByCustomer } from "../../../hooks";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { useRoles } from "../../../security/RolesProvider/RolesProvider";
import { upsertBusinessTask } from "../../../redux/activity/activityActions";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../formsComponents";
import {
  CustomDatePicker,
  CustomTimePicker,
} from "../../formsComponents/CustomDatePicker";
import { UsersAutoSearchByRole } from "../../AutoCompleteSearches/UsersAutoSearchByRole";
import { useCurrentUserId } from "../../../hooks/useAssignedTo";
import { FileUpload } from "../../../features/FileUpload/components/FileUpload";
import { RightPanel } from "../../RightPanel";
import { Save } from "@mui/icons-material";
import { ICustomerFullDetails } from "../../../redux/customer/customer.types";
import { CustomerAutoSearch } from "../../AutoCompleteSearches/CustomerAutoSearch";
import { ActivityCommentsByTask } from "./ActivityCommentsByTask";
import { a11yProps } from "../../Tabs/utils";
import { TabPanel } from "../../Tabs/TabPanel";
import { ActivityHistoryLogs } from "../ActivityHistory";
import produce from "immer";

const accountTypes = [
  {
    label: "Payroll & Compensation",
    values: [
      "Payroll",
      "Commissions",
      "Bonuses",
      "Benefits & Deductions",
      "T4 / T4A",
      "Records of Employment (ROE)",
      "Expense Reimbursements",
    ],
  },
  {
    label: "Accounts Payable (A/P)",
    values: [
      "Vendor Invoices",
      "Rent & Utilities",
      "Office Supplies",
      "Professional Fees (Legal, Accounting, IT)",
      "Marketing & Advertising Expenses",
      "Insurance Premium Payments",
    ],
  },
  {
    label: "Accounts Receivable (A/R)",
    values: [
      "Customer Payments",
      "Premium Collections",
      "Refunds & Adjustments",
      "Outstanding Balances / Overdue Accounts",
      "Direct Bill / Agency Bill Reconciliation",
      "Corrections",
    ],
  },
  {
    label: "Banking & Cash Management",
    values: [
      "Bank Deposits",
      "EFTs / Wire Transfers",
      "Petty Cash",
      "Bank Reconciliation",
      "NSF Payments & Chargebacks",
    ],
  },
  {
    label: "Taxation & Compliance",
    values: [
      "HST / GST Filings",
      "Payroll Remittances",
      "Income Tax Preparation",
      "WSIB / EHT Filings",
      "CRA Correspondence",
    ],
  },
  {
    label: "Financial Reporting",
    values: [
      "Monthly Financial Statements",
      "Budgeting & Forecasting",
      "Variance Analysis",
      "Year-End Audit Support",
      "Management Reports",
    ],
  },
  {
    label: "Customer & Broker Transactions",
    values: [
      "Customer Refunds",
      "Broker Commission Settlements",
      "Policy Adjustments / Endorsements",
      "Reconciliation with Insurers",
    ],
  },
  {
    label: "Other Administrative",
    values: [
      "Record Keeping",
      "System Entries / Data Integrity",
      "Internal Controls",
      "Approvals & Authorization Tracking",
      "Audit Requests",
    ],
  },
];

export const ManageBusinessTask: React.FC<IManageBusinessTaskProps> = (
  props,
) => {
  const {
    moduleInformation: { moduleId, moduleName, refColumnName, subModuleName },
    open,
    onClose,
    task = INITIAL_BUSINESS_TASK,
    onComplete,
    showCustomerAndPolicy = false,
    customer_id,
    customer_policy_id,
    showCommentsByTask = false,
    disableTaskType = false,
  } = props;
  const isUpdate = task && task.task_module_wise_code ? true : false;
  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, value: any) => {
    setSelectedTab(value);
  };

  const { policiesList, fetchPoliciesByCustomerId } = usePoliciesByCustomer();

  const isDisableDiscription = isUpdate && task.task_type === "Notes";

  const dispatch = useDispatchWrapper();
  const {
    user: { userId },
  } = useAuth();
  const { firstName, lastName } = useRoles();
  const currentUserId = useCurrentUserId();
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik<IBusinessTask>({
    initialValues: {
      ...task,
      created_by_id: !isUpdate ? userId : task.created_by_id,
      created_by_name: !isUpdate
        ? firstName + " " + lastName
        : task.created_by_name,
      assigned_to_id: !isUpdate ? (currentUserId as any) : task.assigned_to_id,
      assigned_to_name: !isUpdate
        ? firstName + " " + lastName
        : task.assigned_to_name,
      customer_id: !isUpdate ? customer_id : task.customer_id,
      customer_policy_id: !isUpdate
        ? customer_policy_id
        : task.customer_policy_id,
    },
    validate: (values) => {
      const errors: any = {};

      if (
        values.task_type === "Approval" &&
        values.status === "INACTIVE" &&
        values.approval_status === "Approved"
      ) {
        if (!values.customer_id) {
          errors.customer_id = "Customer is required";
        }
        if (!values.customer_policy_id) {
          errors.customer_policy_id = "Policy is required";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      const requiredCustomerAndPolicy =
        values.task_type === "Approval" && isUpdate;
      dispatch(
        upsertBusinessTask(
          {
            ...values,
            status: values.status || "ACTIVE",
            module_name: requiredCustomerAndPolicy
              ? values.module_name
              : moduleName,
            module_reference_code_or_id: requiredCustomerAndPolicy
              ? values.module_reference_code_or_id
              : moduleId || values.customer_policy_id,
            sub_module_name: subModuleName,
            module_reference_column: requiredCustomerAndPolicy
              ? values.module_reference_column
              : (refColumnName ?? null),
          },
          {
            ...task,
            status: values.status || task.status,
          },
          isUpdate,
          async (isSuccess) => {
            if (isSuccess) {
              //dispatch(fetchBusinessTasksAsync(moduleId));
              onComplete();
            }
          },
        ),
      );
    },
  });

  const requiredCustomerAndPolicy =
    values.task_type === "Approval" &&
    values.status === "INACTIVE" &&
    values.approval_status === "Approved";

  const handleCustomer = (data: ICustomerFullDetails) => {
    if (data.customer_id) {
      setValues({
        ...values,
        customer_id: data.customer_id as number,
        customer_name: data.customer_name,
        policy_number: null,
      });
    }
  };

  const handlePolicyChange = (e: SelectChangeEvent<unknown>) => {
    const policyId = e.target.value as string;
    const policy = policiesList.find(
      (x) => x.customer_policy_id === Number(policyId),
    );
    if (policy) {
      setValues({
        ...values,
        module_reference_column: requiredCustomerAndPolicy
          ? "customer_policy_id"
          : (policyId as any),
        module_reference_code_or_id: policyId as any,
        module_name: requiredCustomerAndPolicy
          ? policy.line_of_business
          : values.module_name,
        policy_number: policy.policy_number,
        producer_one_code: policy.producer_one_code,
        branch_code: policy.branch_code,
        customer_policy_id: policyId as any,
      });
    }
  };

  const handleTaskType = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draftValues) => {
      draftValues.task_type = value;
      draftValues.accounting_type = null;
      draftValues.accounting_sub_type = null;
    });
    setValues(newValues);
  };

  React.useEffect(() => {
    if (customer_policy_id && policiesList.length > 0 && !isUpdate) {
      const policy = policiesList.find(
        (x) => x.customer_policy_id === Number(customer_policy_id),
      );
      if (policy) {
        setValues((prevValues) => ({
          ...prevValues,
          customer_policy_id: customer_policy_id,
          policy_number: policy.policy_number,
          module_reference_code_or_id: customer_policy_id.toString(),
        }));
      }
    } else {
      if (isUpdate && task.task_type !== "Approval") {
        setValues(task);
      }
    }
  }, [isUpdate, task, customer_policy_id, policiesList]);

  React.useEffect(() => {
    if (values.customer_id) {
      fetchPoliciesByCustomerId(values.customer_id as number);
    }
  }, [values.customer_id]);

  console.log("task", task);

  return (
    <RightPanel
      open={open}
      heading="Create Task"
      onClose={onClose}
      isWrappedWithForm={selectedTab === 0}
      width="80%"
      onFormSubmit={handleSubmit}
      actionButtons={
        selectedTab !== 0
          ? undefined
          : () => {
              return (
                <Stack spacing={2} direction={"row"}>
                  <Button
                    variant="contained"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    // disabled={loading}
                    disabled={task.status === "INACTIVE"}
                    startIcon={<Save />}
                  >
                    Save
                  </Button>
                </Stack>
              );
            }
      }
    >
      {showCommentsByTask && (
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          sx={{ mb: 2 }}
        >
          <Tab
            label="Task Details"
            sx={{ fontSize: "1.1rem", fontWeight: 600 }}
            {...a11yProps(0)}
          />
          <Tab
            sx={{ fontSize: "1.1rem", fontWeight: 600 }}
            label="Comments"
            disabled={!task.task_module_wise_code}
            {...a11yProps(1)}
          />
          <Tab
            sx={{ fontSize: "1.1rem", fontWeight: 600 }}
            label="History"
            disabled={!task.task_module_wise_code}
            {...a11yProps(2)}
          />
        </Tabs>
      )}

      <TabPanel index={0} value={selectedTab}>
        <Grid container spacing={1}>
          {(showCustomerAndPolicy || requiredCustomerAndPolicy) && (
            <>
              <Grid item xs={12} md={6}>
                <CustomerAutoSearch
                  label="Customer"
                  value={values.customer_id}
                  onSelect={handleCustomer}
                  error={errors.customer_id}
                  helperText={errors.customer_id}
                  disabled={customer_id ? true : false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Policy</CustomFormLabel>
                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  fullWidth
                  displayEmpty
                  disabled={customer_policy_id ? true : false}
                  name="customer_policy_id"
                  value={
                    Number(values.customer_policy_id) ||
                    Number(values.module_reference_code_or_id)
                  }
                  onChange={handlePolicyChange}
                  placeholder="Select One"
                  options={policiesList.map((item) => {
                    return {
                      label: item.policy_number,
                      value: item.customer_policy_id,
                    };
                  })}
                  helperText={errors.customer_policy_id}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} lg={12}>
            <CustomFormLabel>Task Name</CustomFormLabel>
            <CustomTextField
              name="task_name"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={values.task_name}
              error={errors.task_name ? true : false}
              helperText={errors.task_name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Task Type</CustomFormLabel>
            <ControlledCustomSelect
              name="task_type"
              fullWidth
              value={values.task_type}
              disabled={disableTaskType}
              options={[
                { label: "Accounting", value: "Accounting" },
                { label: "Approval", value: "Approval" },
                { label: "Calls", value: "Calls" },
                { label: "Customer Request", value: "Customer Request" },
                { label: "Emails", value: "Emails" },
                { label: "Events", value: "Events" },
                { label: "Follow-up", value: "Follow-up" },
                { label: "Invoice", value: "Invoice" },
                { label: "Meeting", value: "Meeting" },
                { label: "Notes", value: "Notes" },
                { label: "PFA", value: "PFA" },
                { label: "Question", value: "Question" },
                { label: "Quote", value: "Quote" },
                { label: "Missing Information", value: "Missing Information" },
                { label: "Underwriting", value: "Underwriting" },
                {label: 'Excluded Driver', value: 'Excluded Driver'}
              ]}
              onChange={handleTaskType}
              displayEmpty
              placeholder="Select one"
            />
          </Grid>

          {values.task_type && values.task_type === "Accounting" && (
            <>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Accounting Type</CustomFormLabel>
                <ControlledCustomSelect
                  name="accounting_type"
                  fullWidth
                  value={values.accounting_type || ""}
                  options={accountTypes.map((type) => ({
                    label: type.label,
                    value: type.label,
                  }))}
                  onChange={(e) => {
                    setFieldValue("accounting_type", e.target.value);
                    setFieldValue("accounting_sub_type", ""); // reset sub type
                  }}
                  displayEmpty
                  placeholder="Select Accounting Type"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Accounting Sub-Type</CustomFormLabel>
                <ControlledCustomSelect
                  name="accounting_sub_type"
                  fullWidth
                  value={values.accounting_sub_type || ""}
                  options={
                    accountTypes
                      .find((type) => type.label === values.accounting_type)
                      ?.values.map((sub) => ({
                        label: sub,
                        value: sub,
                      })) || []
                  }
                  onChange={handleChange}
                  displayEmpty
                  placeholder="Select Accounting Sub-Type"
                  disabled={!values.accounting_type}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Status</CustomFormLabel>
            <ControlledCustomSelect
              name="status"
              fullWidth
              value={values.status}
              options={[
                { label: "Open", value: "ACTIVE" },
                { label: "Closed", value: "INACTIVE" },
              ]}
              onChange={handleChange}
              displayEmpty
              placeholder="Select one"
            />
          </Grid>
          {values.task_type === "Approval" && (
            <>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Approval Status</CustomFormLabel>
                <ControlledCustomSelect
                  name="approval_status"
                  fullWidth
                  value={values.approval_status}
                  onChange={handleChange}
                  displayEmpty
                  placeholder="Select one"
                  options={[
                    { label: "Approved", value: "Approved" },
                    { label: "Declined", value: "Declined" },
                    { label: "More Info Needed", value: "More Info Needed" },
                    { label: "More Info Added", value: "More Info Added" },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Line of Business</CustomFormLabel>
                <ControlledCustomSelect
                  name="line_of_business"
                  fullWidth
                  value={values.line_of_business}
                  onChange={handleChange}
                  displayEmpty
                  placeholder="Select one"
                  options={[
                    { label: "CL", value: "CL" },
                    { label: "PL", value: "PL" },
                  ]}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} lg={12}>
            <CustomFormLabel>Description</CustomFormLabel>
            <CustomTextField
              name="description"
              variant="outlined"
              size="small"
              type="text"
              multiline
              rows={6}
              disabled={isDisableDiscription}
              fullWidth
              value={values.description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Task Priority</CustomFormLabel>
            <ControlledCustomSelect
              name="task_priority"
              fullWidth
              value={values.task_priority}
              options={[
                { label: "High", value: "High" },
                { label: "Medium", value: "Medium" },
                { label: "Low", value: "Low" },
              ]}
              onChange={handleChange}
              displayEmpty
              placeholder="Select one"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Date Created</CustomFormLabel>
            <CustomDatePicker
              fullWidth
              value={values.date_created || ""}
              disabled
              onChange={(newValue) => {
                setFieldValue("date_created", newValue);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Due Date</CustomFormLabel>
            <CustomDatePicker
              fullWidth
              value={values.due_date || ""}
              onChange={(newvalue) => {
                setFieldValue("due_date", newvalue);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Due Time</CustomFormLabel>
            <CustomTimePicker
              fullWidth
              value={values.due_time as any}
              onChange={(newValue) => {
                setFieldValue("due_time", newValue.toString());
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Date Completed</CustomFormLabel>
            <CustomDatePicker
              fullWidth
              value={values.date_completed || ""}
              disabled
              onChange={(newValue) => {
                setFieldValue("date_completed", newValue);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Time Completed</CustomFormLabel>
            <CustomTimePicker
              fullWidth
              disabled
              value={values.time_completed}
              onChange={(newValue) => {
                setFieldValue("time_completed", newValue);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Assigned To</CustomFormLabel>
            <UsersAutoSearchByRole
              value={values.assigned_to_id || ""}
              onSelect={(newvalue) => {
                setValues({
                  ...values,
                  assigned_to_id: newvalue.user_fact_id as any,
                  assigned_to_name:
                    newvalue.first_name + " " + newvalue.last_name,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Created By</CustomFormLabel>
            <CustomTextField
              name="created_by_name"
              variant="outlined"
              size="small"
              type="text"
              fullWidth
              value={values.created_by_name}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <CustomFormLabel>File</CustomFormLabel>
            <FileUpload
              multiple
              value={values.file_upload || []}
              onMultiChange={(data) => setFieldValue("file_upload", data)}
              onDelete={() => {
                setFieldValue("file_upload", null);
              }}
              deleteDisabled={task.status === "INACTIVE"}
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel index={1} value={selectedTab}>
        {task.task_module_wise_code && (
          <ActivityCommentsByTask
            moduleId={task.task_module_wise_code as string}
          />
        )}
      </TabPanel>

      <TabPanel index={2} value={selectedTab}>
        <ActivityHistoryLogs
          moduleInformation={{
            moduleName: "Acitivties",
            moduleId: task.task_module_wise_code as string,
            refColumnName: "task_module_wise_code",
            subModuleName: "",
          }}
        />
      </TabPanel>
    </RightPanel>
  );
};
